/**
 * What a missed call was (T-058): written by the Aircall webhook into
 * `communications.metadata.call_context`, read here so the inbox row, the
 * toast, the thread header and the feed's call card all say the same thing.
 *
 * Closed / one-line form (inbox row, toast, closed feed card):
 *     Hung up in menu · 12 s · Ad call: cremation montreal · 2nd call in 24 h
 * Expanded form (feed card opened): a key/value list, see `callContextDetails`.
 */

export type IvrHangupKind = 'known' | 'repeat' | 'ad' | 'quiet';

export interface CallContext {
  dialed: string | null;
  line: string | null;
  /** QC_EN | QC_FR | ON when it rang on a local line. */
  line_market: string | null;
  seconds: number | null;
  after_hours: boolean;
  ivr_hangup: IvrHangupKind | null;
  /** Earlier menu hang-ups from the person in the 24 h before this one. */
  prior_hangups_24h?: number | null;
  /** false = no toast / desktop notification for this call. */
  notify: boolean;
  /** Present for an ad call; fields null when no ad session matched. */
  ad: { keyword: string | null; landing_page: string | null; campaign: string | null } | null;
}

/** The call a context line describes. */
export interface CallFacts {
  direction: 'inbound' | 'outbound' | null | undefined;
  /** answered | missed | voicemail (null on old rows). */
  status: string | null | undefined;
  /** Aircall's duration (includes ringing); fallback for `seconds`. */
  durationSeconds?: number | null;
}

const LINE_LABEL: Record<string, string> = {
  QC_FR: 'QC French',
  QC_EN: 'QC English',
  ON: 'Ontario',
};

const MAIN_LINE = '+14388171770';

/** Accepts the raw jsonb, whatever shape an old or partial row has. */
export function asCallContext(raw: unknown): CallContext | null {
  if (!raw || typeof raw !== 'object') return null;
  return raw as CallContext;
}

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

/** "12 s", "1 min 5 s". */
export function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m} min ${s} s` : `${m} min`;
}

function seconds(ctx: CallContext, call: CallFacts): number | null {
  const s = ctx.seconds ?? call.durationSeconds ?? null;
  return s != null && s >= 0 ? s : null;
}

/** The headline: what happened to the call. */
export function callOutcome(ctx: CallContext | null, call: CallFacts): string | null {
  if (call.direction !== 'inbound' || call.status === 'answered') return null;
  if (call.status === 'voicemail') return 'Voicemail';
  if (ctx?.ivr_hangup) return 'Hung up in menu';
  const line = ctx?.line_market ? LINE_LABEL[ctx.line_market] : null;
  if (line) return `No answer on ${line} line`;
  return 'Missed call';
}

/** "Ad call", "Ad call: cremation montreal", or with landing + campaign. */
export function adLabel(ctx: CallContext | null, full = false): string | null {
  if (!ctx?.ad) return null;
  const parts = [ctx.ad.keyword, ...(full ? [ctx.ad.landing_page, ctx.ad.campaign] : [])].filter(
    (p): p is string => !!p,
  );
  return parts.length ? `Ad call: ${parts.join(' · ')}` : 'Ad call';
}

function repeatLabel(ctx: CallContext | null): string | null {
  const prior = ctx?.prior_hangups_24h;
  return prior && prior > 0 ? `${ordinal(prior + 1)} call in 24 h` : null;
}

/**
 * Option A: the outcome, then the facts after it, most useful first. Null
 * when the call has no context worth more than today's wording (answered,
 * outbound, or an old row with nothing recorded).
 *
 * `full` adds the ad's landing page and campaign (toast, thread header).
 */
export function callContextLine(
  ctx: CallContext | null,
  call: CallFacts,
  { full = false }: { full?: boolean } = {},
): { outcome: string; rest: string[] } | null {
  if (!ctx || call.direction !== 'inbound') return null;
  const outcome = callOutcome(ctx, call);
  const ad = adLabel(ctx, full);
  if (!outcome) {
    // An answered ad call still says where it came from.
    return ad && call.status === 'answered' ? { outcome: ad, rest: [] } : null;
  }
  const s = seconds(ctx, call);
  const time =
    s == null ? null : ctx.ivr_hangup || call.status === 'voicemail' ? formatSeconds(s) : `rang ${formatSeconds(s)}`;
  const rest = [time, ad, repeatLabel(ctx), ctx.after_hours ? 'After hours' : null].filter(
    (p): p is string => !!p,
  );
  return { outcome, rest };
}

/** The same line as plain text. */
export function callContextText(
  ctx: CallContext | null,
  call: CallFacts,
  opts?: { full?: boolean },
): string | null {
  const line = callContextLine(ctx, call, opts);
  return line ? [line.outcome, ...line.rest].join(' · ') : null;
}

/** Option C's expanded panel: label / value pairs, only the ones we know. */
export function callContextDetails(
  ctx: CallContext | null,
  call: CallFacts,
): { label: string; value: string }[] {
  if (!ctx) return [];
  const rows: { label: string; value: string }[] = [];
  const outcome = callOutcome(ctx, call);
  if (outcome) rows.push({ label: 'Outcome', value: outcome });
  const s = seconds(ctx, call);
  if (s != null) {
    rows.push({
      label: 'On the line',
      value: ctx.ivr_hangup || call.status !== 'missed' ? formatSeconds(s) : `Rang ${formatSeconds(s)}`,
    });
  }
  const market = ctx.line_market ? LINE_LABEL[ctx.line_market] : null;
  if (market) rows.push({ label: 'Line', value: `${market} line` });
  else if (ctx.line === MAIN_LINE) {
    rows.push({ label: 'Line', value: ctx.ivr_hangup ? 'Main line · no market picked' : 'Main line' });
  }
  if (ctx.ad) {
    rows.push({ label: 'Ad', value: 'Ad call' });
    if (ctx.ad.keyword) rows.push({ label: 'Keyword', value: ctx.ad.keyword });
    if (ctx.ad.landing_page) rows.push({ label: 'Landing page', value: ctx.ad.landing_page });
    if (ctx.ad.campaign) rows.push({ label: 'Campaign', value: ctx.ad.campaign });
  }
  const repeat = repeatLabel(ctx);
  if (repeat) rows.push({ label: 'Repeat', value: repeat });
  rows.push({ label: 'Hours', value: ctx.after_hours ? 'After hours' : 'Business hours' });
  return rows;
}
